<template>
    <BasicModalWindow
      @modalWindowClose="onModalClose()"
      ref="modal_window_component"
      class="contacts-modal"
      title="contacts.add_to_group_title"
    >
        <template #pre-body>
          <InputSearch
            class="contacts-search"
            @inputSearchSet='onSearchSet'
            @inputSearchClear='onSearchClear'
           />
        </template>

        <template #body>
            <ContactsListRadio ref="contacts_list_component"
              @contactSelect='onContactSelect'
              :contacts_selection='groups_selection'
              :contacts_list="groups_list"
            />
        </template>

        <template #post-body v-if="create_group_visibility">
            <div class="create-group">
                <div class="form">
                    <fieldset>
                        <div class="control-group">
                            <div class="control">
                                <label for="new_group_name">{{Language.say('contacts.form.group_add')}}</label>
                                <div class="close" @click="onCreateGroupClose()"><span></span></div>
                                <span class="attach-button">
                                    <input id="new_group_name" @keyup.enter="onCreateGroupSubmit()" type="text" v-focus="true" v-model="input_group_name">
                                    <ActionButton
                                        @click="onCreateGroupSubmit()"
                                        icon="fas fa-check"
                                        class='main-action'
                                        text="global.actions.confirm"
                                        :loading="contacts_controller.state.events['group_save'] === 'loading'"
                                        :disabled='!input_group_name'
                                    />
                                </span>
                            </div>
                        </div>
                  </fieldset>
                </div>
            </div>
        </template>
        <template #footer>
            <ActionButton
                @click="onCreateGroupOpen()"
                class='outline push-left'
                icon="fas fa-user-plus group-plus"
                text="contacts.actions.add_group"
                :disabled="create_group_visibility"
              />

            <ActionButton
                @click="onSelectedGroupSubmit()"
                icon="fa fa-folder-open"
                class='main-action push-right'
                text="contacts.actions.add_to_group"
                :loading="contacts_controller.state.events['group_add_persons'] === 'loading'"
                :disabled="!groups_selection.count || create_group_visibility"
            />
        </template>
    </BasicModalWindow>
</template>

<script setup>
  import {ref, nextTick, reactive}   from 'vue';
  import {defineProps, defineEmits}  from 'vue';
  import {onBeforeMount}             from 'vue';
  import ContactsListRadio           from '../Partials/ContactsListRadio.vue';
  import InputSearch                 from '@/vue/Elements/Inputs/InputSearch.vue';
  import ActionButton                from '@/vue/Elements/Buttons/ActionButton.vue'
  import BasicModalWindow            from '@/vue/Layouts/Modals/BasicModalWindow.vue';
  import ContactsController          from '@/js/Contacts/ContactsController';
  import ContactsSelectionCollection from '@/js/Contacts/Collections/ContactsSelectionCollection';
  import ContactsFilterCollection    from '@/js/Contacts/Collections/ContactsFilterCollection';
  import ContactGroupItem            from '@/js/Contacts/Entities/ContactGroupItem';
  import Language                    from '@/js/Shared/Language/Language.js';

  const props   = defineProps({
    contacts_selection : {
      required: true,
    }
  })

  const emit = defineEmits([
    'contactsToGroupSubmit', 'contactsToGroupCancel'
  ])

  const input_group_name        = ref();
  const create_group_visibility = ref(false);
  const contacts_list_component = ref();
  const contacts_controller     = ContactsController;
  const groups_selection        = reactive(new ContactsSelectionCollection());
  const groups_list             = reactive(new ContactsFilterCollection());

  onBeforeMount(() => {
    groups_list.setSearchList(contacts_controller.contacts.groups);
    groups_list.reload();
  })

  const onSearchSet = (term) => {
    groups_selection.reset();
    groups_list.searchIn('groups', term);
  }

  const onSearchClear = () => {
    groups_selection.reset();
    groups_list.reload();
  }

  const onCreateGroupOpen = () => {
    groups_selection.reset();
    setCreateGroupVisibility(true);
  }

  const onCreateGroupClose = () => {
    setCreateGroupVisibility(false);
  }

  const onModalClose = () => {
    emit('contactsToGroupCancel');
  }

  const onContactSelect = () => {
    setCreateGroupVisibility(false);
  }

  const onSelectedGroupSubmit = async () => {
    if (await contacts_controller.addPersonsToGroup(
        groups_selection.getFirst(),
        props.contacts_selection.getIndexKeys()) === false) {
      return;
    }

    emit('contactsToGroupSubmit');
  }

  const onCreateGroupSubmit = async () => {
    if (await contacts_controller.saveGroup(createGroupItem(), 'contacts-reload') === false) {
      return;
    }

    clearInputGroupName();
    setCreateGroupVisibility(false);
    groups_list.reload();
    await nextTick();
    let contact = contacts_controller.contacts.groups.getLastAdded();
    groups_selection.unique(contact);
    contacts_list_component.value.scrollToContact(contact);
  }

  const createGroupItem = () => {
    let group  = new ContactGroupItem();
    group.name = input_group_name.value;
    return group;
  }

  const clearInputGroupName = () => {
    input_group_name.value = null;
  }

  const setCreateGroupVisibility = (value) => {
    create_group_visibility.value = value;
  }
</script>
